// Layer 4 (UI) — the Risk route (polish.P20, locked comp
// `.impeccable/mocks/decision/risk.html`, A with B's US$ bars and C's Critical
// controls tab). The family grammar: one header row carrying the plant's
// constraint, one status band, an 8/4 panel row — where the nine risks sit
// and the three that matter, beside what waits on the GM — then three ink pill
// tabs, each with its derived headline. The overview tab dissolves into the
// top of the page (adjudication 1); Risk register is the default for every
// persona. The tab rides the URL (`?tab=`).

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { usePersona } from '@/app/providers';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE_LARGE } from '@/shared/ui-class';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { SYN_CHIP } from '../downtime/DowntimeCard';
import { CriticalControls } from './CriticalControls';
import { RiskBand } from './RiskBand';
import { riskBand, riskHeaderFact, riskTabHeadlines } from './RiskBand.transform';
import { RiskRegisterTab } from './RiskRegister';
import { ShiftChecklist } from './ShiftChecklist';
import { WaitingOnYou } from './WaitingOnYou';
import { waitingOnYou, type GateDecision } from './WaitingOnYou.transform';
import { WhereRiskSits } from './WhereRiskSits';
import {
  RISK_TABS,
  isRiskTab,
  riskCopy,
  riskRegister,
  type RiskConsequenceMode,
  type RiskTab,
} from './Scene.transform';

export function RiskScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const copy = riskCopy();
  const { persona } = usePersona();
  const { state } = useScenario();
  const [tab, setTab] = useState<RiskTab>('risk-register');
  const [mode, setMode] = useState<RiskConsequenceMode>('worst');
  const [selectedCell, setSelectedCell] = useState<string | null>(null);
  const rows = riskRegister();
  const [selectedId, setSelectedId] = useState<string | undefined>(rows[0]?.id);
  const [gateDecision, setGateDecision] = useState<GateDecision | null>(null);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (isRiskTab(tabParam)) setTab(tabParam);
  }, [searchParams]);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const slug = projectId ?? 'alpha';
  const header = riskHeaderFact(state);
  const headlines = riskTabHeadlines();
  const tabItems = RISK_TABS.map((item) => ({ ...item, count: headlines[item.id] }));
  const waiting = waitingOnYou(persona, gateDecision);

  const selectTab = (value: string) => {
    if (!isRiskTab(value)) return;
    setTab(value);
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="RiskScene">
        <SurfacePageBody className="space-y-3" width="full">
          <header className="flex flex-wrap items-center gap-x-6 gap-y-2" data-risk-header>
            <div className="mr-auto flex min-w-0 items-center gap-3">
              <h1 className={cn(SURFACE_PAGE_TITLE_LARGE, 'min-w-0')}>{copy.title}</h1>
              {SYN_CHIP}
            </div>
            <div className="flex items-center gap-3">
              <dl
                className="flex min-w-0 flex-col gap-px border-l border-[var(--hairline-strong)] pl-4 tabular-nums"
                data-header-fact="constraint"
              >
                <dt className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Constraint</dt>
                <dd className="text-[13.5px] font-semibold leading-tight text-ink-primary">{header.circuit}</dd>
                <dd className="whitespace-nowrap text-xs text-muted">{header.comparison}</dd>
              </dl>
              <HelpAnchor doc="risk/overview" label="Risk" />
            </div>
          </header>

          <Commentable name="RiskBand">
            <RiskBand cells={riskBand()} />
          </Commentable>

          <div className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-12">
            <div className="min-w-0 xl:col-span-8">
              <Commentable name="RiskOverview">
                <WhereRiskSits
                  mode={mode}
                  onModeChange={(next) => {
                    setMode(next);
                    setSelectedCell(null);
                  }}
                  selectedCell={selectedCell}
                  onSelectCell={setSelectedCell}
                />
              </Commentable>
            </div>
            <div className="min-w-0 xl:col-span-4">
              <Commentable name="WaitingOnYou">
                <WaitingOnYou
                  read={waiting}
                  projectId={slug}
                  onDecide={(kind) => setGateDecision({ kind, at: state.cursor })}
                />
              </Commentable>
            </div>
          </div>

          <SurfacePageTabs
            aria-label="Risk"
            items={tabItems}
            value={tab}
            onChange={selectTab}
            variant="ink"
            className="pt-1"
          />

          {tab === 'risk-register' ? (
            <Commentable name="RiskRegister">
              <RiskRegisterTab rows={rows} selectedId={selectedId} onSelect={setSelectedId} />
            </Commentable>
          ) : null}
          {tab === 'checks' ? (
            <Commentable name="ShiftChecklist">
              <ShiftChecklist persona={persona} projectId={slug} />
            </Commentable>
          ) : null}
          {tab === 'controls' ? (
            <Commentable name="CriticalControls">
              <CriticalControls />
            </Commentable>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
